import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut, ArrowLeft, Loader2, Save, UploadCloud, Package as PackageIcon } from "lucide-react";
import { fetchBlogs, createBlog, updateBlog, deleteBlog, uploadImage, adminMe, clearToken, getToken } from "../lib/api";
import { COMPANY } from "../data/site";

const EMPTY = {
  title: "", slug: "", category: "", author: "Team Life Line", date: "",
  read_time: "", image: "", excerpt: "", content: [], featured: false,
};

const paraToText = (a) => (a || []).join("\n\n");
const textToPara = (s) => s.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function AdminBlogs() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await fetchBlogs());
    } catch {
      toast.error("Failed to load stories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    adminMe().then(load).catch(() => { clearToken(); navigate("/admin/login"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => setEditing({ ...EMPTY, date: new Date().toISOString().slice(0, 10) });
  const openEdit = (p) => setEditing({ ...p });
  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      if (editing.id) await updateBlog(editing.id, editing);
      else await createBlog(editing);
      toast.success(editing.id ? "Story updated." : "Story published.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await deleteBlog(p.id);
      toast.success("Story deleted.");
      setRows((r) => r.filter((x) => x.id !== p.id));
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = () => { clearToken(); navigate("/admin/login"); };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB."); return; }
    setUploading(true);
    try {
      const { data } = await uploadImage(file);
      set("image", data.url);
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0A221C] text-white">
        <div className="ll-container py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-lg px-3 py-2"><img src={COMPANY.logo} alt="Life Line" className="h-8 w-auto object-contain" /></span>
            <span className="font-display text-xl font-bold hidden sm:block">Blog Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="admin-nav-enquiries" className="text-sm text-white/80 hover:text-white flex items-center gap-2"><ArrowLeft size={16} /> Enquiries</Link>
            <Link to="/admin/tours" data-testid="admin-nav-tours" className="text-sm text-white/80 hover:text-white flex items-center gap-2"><PackageIcon size={16} /> Tours</Link>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="ll-container py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-[#0A221C]">Travel Stories</h1>
            <p className="text-[#4A5D58] mt-1">{rows.length} stories live on your website</p>
          </div>
          <button data-testid="add-blog-btn" onClick={openNew} className="btn-primary"><Plus size={18} /> Add New Story</button>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#4ef72d]" size={32} /></div>
        ) : rows.length === 0 ? (
          <p className="py-24 text-center text-[#4A5D58]">No stories yet. Click “Add New Story” to publish your first.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blogs-list">
            {rows.map((p) => (
              <div key={p.id} data-testid={`blog-item-${p.slug}`} className="ll-card overflow-hidden">
                <div className="h-40 overflow-hidden bg-black/5 relative">
                  {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover" />}
                  {p.category && <span className="absolute top-3 left-3 badge-dark">{p.category}</span>}
                </div>
                <div className="p-5">
                  <p className="text-xs text-[#4A5D58]">{fmtDate(p.date)} · {p.read_time || "—"}</p>
                  <h3 className="font-display text-lg font-bold text-[#0A221C] leading-tight mt-1">{p.title}</h3>
                  <div className="flex gap-2 mt-4">
                    <button data-testid={`edit-blog-${p.slug}`} onClick={() => openEdit(p)} className="flex-1 flex items-center justify-center gap-1.5 border border-black/15 rounded-full py-2 text-sm font-medium hover:bg-[#0A221C] hover:text-white transition-colors"><Pencil size={14} /> Edit</button>
                    <button data-testid={`delete-blog-${p.slug}`} onClick={() => remove(p)} className="flex items-center justify-center gap-1.5 border border-red-200 text-red-600 rounded-full py-2 px-3 text-sm font-medium hover:bg-red-600 hover:text-white transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => !saving && setEditing(null)} />
          <div className="relative bg-[#FDFBF7] w-full max-w-2xl h-full overflow-y-auto shadow-2xl" data-testid="blog-editor">
            <div className="sticky top-0 bg-[#0A221C] text-white px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-display text-xl font-bold">{editing.id ? "Edit Story" : "Add New Story"}</h2>
              <button onClick={() => !saving && setEditing(null)} className="text-white/80 hover:text-white"><X size={22} /></button>
            </div>

            <div className="p-6 space-y-6">
              <Field label="Title *"><input data-testid="blog-title" className="editor-input" value={editing.title} onChange={(e) => set("title", e.target.value)} /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Category"><input data-testid="blog-category" className="editor-input" value={editing.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Himalayas" /></Field>
                <Field label="Author"><input className="editor-input" value={editing.author} onChange={(e) => set("author", e.target.value)} /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Date"><input type="date" data-testid="blog-date" className="editor-input" value={(editing.date || "").slice(0, 10)} onChange={(e) => set("date", e.target.value)} /></Field>
                <Field label="Read time"><input className="editor-input" value={editing.read_time} onChange={(e) => set("read_time", e.target.value)} placeholder="e.g. 6 min read" /></Field>
              </div>
              <Field label="Slug (auto if blank)"><input className="editor-input" value={editing.slug || ""} onChange={(e) => set("slug", e.target.value)} /></Field>

              <Field label="Cover Image">
                <div
                  data-testid="blog-image-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                  className="border-2 border-dashed border-black/15 rounded-2xl p-5 text-center hover:border-[#4ef72d] transition-colors bg-white"
                >
                  {editing.image ? (
                    <div className="relative">
                      <img src={editing.image} alt="preview" className="h-40 w-full object-cover rounded-xl" />
                      <button type="button" onClick={() => set("image", "")} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black"><X size={14} /></button>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex flex-col items-center gap-2 py-6">
                      {uploading ? <Loader2 className="animate-spin text-[#4ef72d]" size={28} /> : <UploadCloud className="text-[#4A5D58]" size={30} />}
                      <span className="text-sm font-semibold text-[#0A221C]">{uploading ? "Uploading..." : "Drag & drop an image, or click to upload"}</span>
                      <span className="text-xs text-[#4A5D58]">JPG, PNG, WEBP · up to 8MB</span>
                      <input data-testid="blog-image-file" type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                    </label>
                  )}
                </div>
                <input data-testid="blog-image" className="editor-input mt-2" value={editing.image} onChange={(e) => set("image", e.target.value)} placeholder="…or paste an image URL" />
              </Field>

              <Field label="Excerpt (short summary)"><textarea rows={3} data-testid="blog-excerpt" className="editor-input resize-none" value={editing.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></Field>
              <Field label="Content (separate paragraphs with a blank line)">
                <textarea rows={12} data-testid="blog-content" className="editor-input resize-none" value={paraToText(editing.content)} onChange={(e) => set("content", textToPara(e.target.value))} placeholder={"First paragraph...\n\nSecond paragraph..."} />
              </Field>

              <label className="flex items-center gap-2 text-sm font-medium text-[#0A221C]">
                <input type="checkbox" checked={editing.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#4ef72d]" /> Mark as featured
              </label>
            </div>

            <div className="sticky bottom-0 bg-[#FDFBF7] border-t border-black/5 px-6 py-4 flex gap-3">
              <button onClick={() => !saving && setEditing(null)} className="btn-outline flex-1">Cancel</button>
              <button data-testid="blog-save" onClick={save} disabled={saving} className="btn-primary flex-1 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} {saving ? "Saving..." : "Save Story"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="eyebrow text-[#4A5D58] block mb-2">{label}</label>
    {children}
  </div>
);
